import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fallbackData } from '@/lib/fallbackData';
import { loadContentFromMarkdown } from '@/lib/contentLoader';
import { getSiteData, saveSiteData } from '@/lib/dataService';
import { normalizeMediaUrl } from '@/lib/mediaUrl';
import { hasSupabaseConfig } from '@/lib/supabaseClient';
import { FOX_ESS_30KW_IMAGE_URL, withBeastProduct } from '@/lib/beastProduct';
import { normalizeFoxEssBatteryProducts } from '@/lib/foxEssBatteries';

const STORAGE_KEY = 'glc-data';
const SOCIAL_LINKS = {
  social_facebook: 'https://www.facebook.com/greenlightcityGLC',
  social_instagram: 'https://www.instagram.com/green_light_city/',
  social_linkedin: 'https://www.linkedin.com/company/green-light-city-sa/about/',
};

const DataContext = createContext(null);

const normalizeArrayMedia = (records = [], keys = ['image_url']) =>
  (Array.isArray(records) ? records : []).map((record) =>
    keys.reduce((next, key) => {
      if (!next || typeof next !== 'object' || !Object.prototype.hasOwnProperty.call(next, key)) {
        return next;
      }

      const normalizedValue = normalizeMediaUrl(next[key]);
      return normalizedValue === next[key] ? next : { ...next, [key]: normalizedValue };
    }, record)
  );

const normalizeData = (incoming) => ({
  ...incoming,
  company: {
    ...(incoming?.company || {}),
    ...SOCIAL_LINKS,
    suppliers: normalizeArrayMedia(incoming?.company?.suppliers || [], ['logo_url']),
  },
  team: normalizeArrayMedia(incoming?.team || []),
  projects: normalizeArrayMedia(incoming?.projects || []),
  blog: normalizeArrayMedia(incoming?.blog || []),
  products: normalizeFoxEssBatteryProducts(withBeastProduct(incoming?.products || [])).map((product) => {
    const isJaSolar455 =
      String(product?.brand).toLowerCase() === 'jasolar' &&
      /455w/i.test(String(product?.name || ''));
    const isJaSolar540 =
      String(product?.brand).toLowerCase() === 'jasolar' &&
      /540w/i.test(String(product?.name || ''));
    const isFoxEss30Kw =
      String(product?.brand).toLowerCase() === 'foxess' &&
      /30\s*kW/i.test(String(product?.name || ''));

    if (isFoxEss30Kw) {
      return {
        ...product,
        image_url: normalizeMediaUrl(FOX_ESS_30KW_IMAGE_URL),
      };
    }

    if (isJaSolar455) {
      return {
        ...product,
        power_range: product.power_range || '430-455W (JAM54D41 LB, DeepBlue 4.0)',
        description:
          product.description ||
          'n-type bifacial double-glass mono module with lower LID, strong low-irradiance response, improved temperature coefficient, and higher energy yield for better LCOE.',
        image_url: normalizeMediaUrl(product.image_url || 'https://jasolar.africa/images/com_hikashop/upload/455w.jpg'),
        datasheet_url:
          product.datasheet_url ||
          'https://jasolar.africa/ja-solar-solutions/product/download/file_id-13',
      };
    }

    if (isJaSolar540) {
      return {
        ...product,
        power_range: product.power_range || 'DeepBlue 4.0 Series (high-power class)',
        description:
          product.description ||
          'High-output JA Solar module option aligned to the DeepBlue 4.0 family with half-cell and multi-busbar architecture for commercial and residential deployments.',
        image_url: normalizeMediaUrl(product.image_url || 'https://jasolar.africa/images/com_hikashop/upload/jam72s41.jpg'),
        datasheet_url:
          product.datasheet_url ||
          'https://jasolar.africa/ja-solar-solutions/product/download/file_id-14',
      };
    }

    const normalizedImage = normalizeMediaUrl(product?.image_url);
    return normalizedImage === product?.image_url ? product : { ...product, image_url: normalizedImage };
  }),
});

const loadStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persist = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState(() => normalizeData(loadStored() || fallbackData));
  const hasPersistedInitialData = useRef(false);
  const [loadedFromRemote, setLoadedFromRemote] = useState(false);
  const [remoteWriteEnabled, setRemoteWriteEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/admin');
  });

  useEffect(() => {
    if (!hasPersistedInitialData.current) {
      hasPersistedInitialData.current = true;
      return;
    }
    persist(data);
  }, [data]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setRemoteWriteEnabled(window.location.pathname.startsWith('/admin'));
  }, []);

  useEffect(() => {
    if (!hasSupabaseConfig) return;
    let active = true;
    let idleId;
    let timeoutId;
    const load = async () => {
      const result = await getSiteData();
      if (!active) return;
      if (result?.data && result.source && !String(result.source).startsWith('fallback')) {
        setData((prev) => normalizeData({ ...prev, ...result.data }));
      }
      setLoadedFromRemote(true);
    };

    if (window.location.pathname === '/') {
      timeoutId = window.setTimeout(() => {
        if ('requestIdleCallback' in window) {
          idleId = window.requestIdleCallback(load, { timeout: 3000 });
        } else {
          load();
        }
      }, 7000);
    } else {
      load();
    }

    return () => {
      active = false;
      if (idleId) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!hasSupabaseConfig || !loadedFromRemote || !remoteWriteEnabled) return;
    const handle = setTimeout(async () => {
      const result = await saveSiteData(data);
      if (result?.error) {
        console.warn('Failed to save site data:', result.error.message || result.error);
        if (result.status === 401 || result.status === 403 || result.status === 404 || (result.status && result.status >= 500)) {
          setRemoteWriteEnabled(false);
          console.warn('Disabling remote site-data autosave after server save failure.');
        }
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [data, loadedFromRemote, remoteWriteEnabled]);

  useEffect(() => {
    const load = async () => {
      const md = await loadContentFromMarkdown();
      if (md) {
        setData((prev) => ({
          ...normalizeData(prev),
          copy: md.copy || prev.copy || {},
        }));
      }
    };
    load();
  }, []);

  const updateItem = (table, id, updates) => {
    setData((prev) => {
      const current = prev[table];
      if (Array.isArray(current)) {
        return normalizeData({
          ...prev,
          [table]: current.map((item) => (item.id === id ? { ...item, ...updates } : item)),
        });
      }
      if (current && typeof current === 'object') {
        return normalizeData({ ...prev, [table]: { ...current, ...updates } });
      }
      return normalizeData({ ...prev, [table]: updates });
    });
  };

  const addItem = (table, item) => {
    setData((prev) => normalizeData({
      ...prev,
      [table]: Array.isArray(prev[table]) ? [...prev[table], item] : prev[table],
    }));
  };

  const removeItem = (table, id) => {
    setData((prev) => normalizeData({
      ...prev,
      [table]: Array.isArray(prev[table]) ? prev[table].filter((item) => item.id !== id) : prev[table],
    }));
  };

  const value = useMemo(
    () => ({
      data,
      updateItem,
      addItem,
      removeItem,
      reset: () => setData(normalizeData(fallbackData)),
    }),
    [data]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);
